import { create } from 'zustand';
import { useOperatorStore } from './useOperatorStore';
import db from '../../db';

/** Map legacy roles to ADMIN so cached sessions keep working after role simplification. */
const normalizeUser = (user) => {
  if (!user) return null;
  if (user.role === 'SUPER_ADMIN') {
    return { ...user, role: 'ADMIN' };
  }
  return user;
};

// Load initial state from IndexedDB
const initializeAuthStore = async (set) => {
  const tokenRecord = await db.auth.get('authToken');
  const userRecord = await db.auth.get('authUser');

  const rawUser = userRecord ? JSON.parse(userRecord.value) : null;
  const user = normalizeUser(rawUser);

  // Persist normalized role so subsequent loads stay consistent
  if (user && rawUser?.role === 'SUPER_ADMIN') {
    await db.auth.put({ key: 'authUser', value: JSON.stringify(user) });
  }

  set({
    token: tokenRecord?.value || '',
    user,
    isAuthenticated: !!tokenRecord?.value,
    isAuthLoaded: true,
  });

  if (tokenRecord?.value && user) {
    await useOperatorStore.getState().bindOperatorForUser(user);
  } else {
    useOperatorStore.setState({
      isOperatorReady: true,
      operatorBindError: null,
    });
  }
};

export const useAuthStore = create((set) => {
  set({ isAuthLoaded: false }); // NEW: Add loading state
  initializeAuthStore(set); // Load auth state asynchronously

  return {
    token: '',
    user: null,
    isAuthenticated: false,
    isAuthLoaded: false, // NEW: Tracks if auth is initialized

    setCredentials: async (newToken, user) => {
      const normalizedUser = normalizeUser(user);
      await db.auth.put({ key: 'authToken', value: newToken });
      await db.auth.put({ key: 'authUser', value: JSON.stringify(normalizedUser) });

      set({
        token: newToken,
        user: normalizedUser,
        isAuthenticated: true,
        isAuthLoaded: true,
      });

      await useOperatorStore.getState().bindOperatorForUser(normalizedUser);
    },

    clearAuth: async () => {
      await db.auth.clear();
      set({
        token: '',
        user: null,
        isAuthenticated: false,
        isAuthLoaded: true,
      });
      useOperatorStore.getState().clearOperatorID();
    },
  };
});
